import React, { useState, useEffect } from 'react';
import { formatTime } from '../data';

// Left-panel display while Auto Plan is running for the active planet (see
// plan.md — Auto Plan integration). Replaces PlanetDisplay so the user isn't
// tempted to manually edit a build order the worker owns.

// Milliseconds -> the same "Xd Xh Xm Xs" style used everywhere else in the
// app (formatTime takes hours), so runtime/staleness read consistently with
// the plan's own total-time display.
const formatDuration = (ms) => formatTime(ms / 3600000);

const AutoPlanStatus = ({ autoPlanRun, onAccept, onDiscard }) => {
  const { status, iteration, bestBuildOrder, bestTime, planetId, errorMessage, startedAt, lastImprovementIteration, lastImprovementAt } = autoPlanRun;
  const stepCount = bestBuildOrder[planetId] ? bestBuildOrder[planetId].length : 0;

  // Local ticking clock so runtime/staleness stay fresh once a second,
  // without forcing App.js (and its unconditional GameReducer call) to
  // re-render on a timer too.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (status !== 'running') return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [status]);

  if (status === 'error') {
    return (
      <section className="w-full h-full overflow-y-auto bg-gray-800 p-4 text-white">
        <div className="bg-gray-600 rounded-lg shadow-lg p-4">
          <h2 className="text-xl font-bold mb-4">Auto Plan failed</h2>
          <p className="text-red-300 mb-4">{errorMessage}</p>
          <button onClick={onDiscard} className="bg-gray-300 text-gray-900 py-1 px-4 rounded">
            Close
          </button>
        </div>
      </section>
    );
  }

  const runtime = startedAt != null ? formatDuration(now - startedAt) : '…';
  const iterationsSinceImprovement = iteration - lastImprovementIteration;
  const sinceImprovement = lastImprovementAt != null ? formatDuration(now - lastImprovementAt) : '…';

  return (
    <section className="w-full h-full overflow-y-auto bg-gray-800 p-4 text-white">
      <div className="bg-gray-600 rounded-lg shadow-lg p-4">
        <h2 className="text-xl font-bold mb-4">Auto Plan running…</h2>
        <div className="grid grid-cols-2 gap-2 mb-6 text-left">
          <span className="text-gray-300">Iterations:</span>
          <span>{iteration.toLocaleString()}</span>
          <span className="text-gray-300">Current best total time:</span>
          <span>{bestTime != null ? formatTime(bestTime) : '…'}</span>
          <span className="text-gray-300">Steps in plan:</span>
          <span>{stepCount}</span>
          <span className="text-gray-300">Runtime:</span>
          <span>{runtime}</span>
          <span className="text-gray-300">Last improvement:</span>
          <span>
            {lastImprovementAt != null ? `${iterationsSinceImprovement.toLocaleString()} iterations ago (${sinceImprovement} ago)` : '…'}
          </span>
        </div>
        <p className="text-sm text-gray-300 mb-4">
          The plan improves as it runs — check the timeline on the right. Stop whenever you're happy with it.
        </p>
        <div className="flex gap-2">
          <button onClick={onAccept} className="bg-green-600 hover:bg-green-700 text-white py-1 px-4 rounded">
            Stop &amp; Keep This Plan
          </button>
          <button onClick={onDiscard} className="bg-gray-300 hover:bg-gray-400 text-gray-900 py-1 px-4 rounded">
            Stop &amp; Discard
          </button>
        </div>
      </div>
    </section>
  );
};

export default AutoPlanStatus;
